import React from 'react';
import { Button } from '../../ui/button';
import type { MigrationStatus } from '../../../utils/cache-migration';
import type { IndexedDBCache } from '../../../utils/indexeddb-cache';

interface CacheManagementProps {

  cacheStats: Awaited<ReturnType<IndexedDBCache["getStats"]>> | null;
  migrationStatus: MigrationStatus | null;
  isMigrating: boolean;
  onMigration: () => void;
  onClearExpired: () => void;
  onClearAll: () => void;
  onClearServiceCache: (service: 'openai' | 'anthropic' | 'gemini' | 'github') => void;
  onRefreshStats: () => void;
  formatBytes: (bytes: number) => string;
}

export const CacheManagement: React.FC<CacheManagementProps> = ({
  cacheStats,
  migrationStatus,
  isMigrating,
  onMigration,
  onClearExpired,
  onClearAll,
  onClearServiceCache,
  onRefreshStats,
  formatBytes
}) => {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Cache Management</h3>

      {migrationStatus && migrationStatus.isRequired && !migrationStatus.isCompleted && (
        <div className="mb-3 rounded-md border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 p-3">
          <h4 className="text-xs font-semibold text-yellow-800 dark:text-yellow-200">Cache Migration Required</h4>
          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 mb-2">
            Migrate old cache data to IndexedDB for better stability and performance.
          </p>
          <Button
            onClick={onMigration}
            disabled={isMigrating}
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700 text-white"
          >
            {isMigrating ? 'Migrating...' : 'Migrate Cache'}
          </Button>
        </div>
      )}

      {cacheStats && (
        <div className="mb-4">
          <div className="rounded-md border border-gray-200 dark:border-gray-700 p-2.5 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Cache</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">{cacheStats.total?.totalEntries || 0} entries</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{formatBytes(cacheStats.total?.totalSize || 0)}</div>
          </div>

          <div className="space-y-1.5">
            {['openai', 'anthropic', 'gemini', 'github'].map((service) => (
              <div key={service} className="flex items-center justify-between rounded-md border border-gray-200 dark:border-gray-700 px-2.5 py-2">
                <div>
                  <div className="text-xs font-medium text-gray-800 dark:text-gray-200 capitalize">{service}</div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {cacheStats[service]?.totalEntries || 0} entries • {formatBytes(cacheStats[service]?.totalSize || 0)}
                  </div>
                </div>
                <Button
                  onClick={() => onClearServiceCache(service as 'openai' | 'anthropic' | 'gemini' | 'github')}
                  size="sm"
                  variant="outline"
                  className="text-xs"
                >
                  Clear
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onClearExpired} variant="outline" size="sm">
          Clear Expired
        </Button>
        <Button
          onClick={onClearAll}
          variant="outline"
          size="sm"
          className="hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300"
        >
          Clear All Cache
        </Button>
        <Button onClick={onRefreshStats} variant="outline" size="sm">
          Refresh Stats
        </Button>
      </div>
    </div>
  );
};
